export const SURVEY_WEBVIEW_AUTH_TOKEN_REQUEST = 'survey-webview-auth-token-request';
export const SURVEY_WEBVIEW_AUTH_TOKEN_RESPONSE = 'survey-webview-auth-token-response';

export interface SurveyWebViewAuthTokenRequest {
  type: typeof SURVEY_WEBVIEW_AUTH_TOKEN_REQUEST;
  requestId: string;
  forceRefresh: boolean;
}

export interface SurveyWebViewAuthTokenResponse {
  type: typeof SURVEY_WEBVIEW_AUTH_TOKEN_RESPONSE;
  requestId: string;
  token?: string;
  error?: string;
}

interface SurveyWebViewAuthTokenClientOptions {
  postMessage(message: string): void;
  subscribe(listener: (data: unknown) => void): () => void;
}

interface SurveyWebViewMessageEventTarget {
  addEventListener(type: 'message', listener: (event: Event) => void): void;
  removeEventListener(type: 'message', listener: (event: Event) => void): void;
}

interface SurveyWebViewAuthBridgeOptions {
  search: string;
  nativeBridge?: { postMessage?(message: string): void };
  windowTarget: SurveyWebViewMessageEventTarget;
  documentTarget: SurveyWebViewMessageEventTarget;
}

interface SurveyWebViewMessageHandlers {
  onAuthTokenRequest(request: SurveyWebViewAuthTokenRequest): void;
  onShowOnARollPopup(): void;
  onCompleted(): void;
  onBack(): void;
  onError(): void;
  onCircuitCompleted?(context: { circuitId: string; mainMissionAssignationId: string }): void;
}

interface PendingTokenRequest {
  fallbackToken?: string;
  forceRefresh: boolean;
  resolve(token?: string): void;
  timeout: ReturnType<typeof setTimeout>;
}

const parseMessage = (data: unknown): unknown => {
  if (typeof data !== 'string') {
    return data;
  }

  try {
    return JSON.parse(data);
  } catch {
    return undefined;
  }
};

export const parseSurveyWebViewAuthTokenRequest = (data: unknown): SurveyWebViewAuthTokenRequest | undefined => {
  const message = parseMessage(data) as Partial<SurveyWebViewAuthTokenRequest> | undefined;

  if (
    message?.type !== SURVEY_WEBVIEW_AUTH_TOKEN_REQUEST
    || typeof message.requestId !== 'string'
    || typeof message.forceRefresh !== 'boolean'
  ) {
    return undefined;
  }

  return {
    type: SURVEY_WEBVIEW_AUTH_TOKEN_REQUEST,
    requestId: message.requestId,
    forceRefresh: message.forceRefresh,
  };
};

export const parseSurveyWebViewAuthTokenResponse = (data: unknown): SurveyWebViewAuthTokenResponse | undefined => {
  const message = parseMessage(data) as Partial<SurveyWebViewAuthTokenResponse> | undefined;

  if (message?.type !== SURVEY_WEBVIEW_AUTH_TOKEN_RESPONSE || typeof message.requestId !== 'string') {
    return undefined;
  }

  return {
    type: SURVEY_WEBVIEW_AUTH_TOKEN_RESPONSE,
    requestId: message.requestId,
    token: typeof message.token === 'string' ? message.token : undefined,
    error: typeof message.error === 'string' ? message.error : undefined,
  };
};

export const createSurveyWebViewAuthTokenResponse = (
  requestId: string,
  token?: string,
  error?: string,
): string => JSON.stringify({
  type: SURVEY_WEBVIEW_AUTH_TOKEN_RESPONSE,
  requestId,
  token,
  error,
} satisfies SurveyWebViewAuthTokenResponse);

export const createNativeSurveyWebViewAuthTokenResponse = async (
  request: SurveyWebViewAuthTokenRequest,
  getIdToken: (forceRefresh: boolean) => Promise<string | undefined>,
): Promise<string> => {
  try {
    const currentToken = await getIdToken(request.forceRefresh);

    return createSurveyWebViewAuthTokenResponse(
      request.requestId,
      currentToken,
      currentToken ? undefined : 'auth-user-unavailable',
    );
  } catch {
    return createSurveyWebViewAuthTokenResponse(request.requestId, undefined, 'auth-token-unavailable');
  }
};

export const handleSurveyWebViewMessage = (
  data: unknown,
  handlers: SurveyWebViewMessageHandlers,
): boolean => {
  const circuitReturn = parseMessage(data) as { type?: string; circuitId?: string; mainMissionAssignationId?: string } | undefined;
  const isGuid = (value: unknown): value is string => typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
  if (circuitReturn?.type === 'competitor-circuit-survey-completed') {
    if (!isGuid(circuitReturn.circuitId) || !isGuid(circuitReturn.mainMissionAssignationId) || !handlers.onCircuitCompleted) return false;
    handlers.onCircuitCompleted({ circuitId: circuitReturn.circuitId, mainMissionAssignationId: circuitReturn.mainMissionAssignationId });
    return true;
  }
  const authTokenRequest = parseSurveyWebViewAuthTokenRequest(data);

  if (authTokenRequest) {
    handlers.onAuthTokenRequest(authTokenRequest);
    return true;
  }

  switch (data) {
    case 'showOnARollPopup':
      handlers.onShowOnARollPopup();
      return true;
    case 'completed':
      handlers.onCompleted();
      return true;
    case 'back':
      handlers.onBack();
      return true;
    case 'error':
      handlers.onError();
      return true;
    default:
      return false;
  }
};

export const createSurveyWebViewAuthTokenClient = ({ postMessage, subscribe }: SurveyWebViewAuthTokenClientOptions) => {
  const pendingRequests = new Map<string, PendingTokenRequest>();
  let requestSequence = 0;

  const unsubscribe = subscribe((data) => {
    const response = parseSurveyWebViewAuthTokenResponse(data);
    const pending = response ? pendingRequests.get(response.requestId) : undefined;

    if (!response || !pending) {
      return;
    }

    clearTimeout(pending.timeout);
    pendingRequests.delete(response.requestId);
    pending.resolve(response.token ?? (pending.forceRefresh ? undefined : pending.fallbackToken));
  });

  return {
    requestToken(fallbackToken?: string, forceRefresh = false, timeoutMs = 10_000): Promise<string | undefined> {
      const requestId = `${Date.now()}-${++requestSequence}`;

      return new Promise((resolve) => {
        const timeout = setTimeout(() => {
          pendingRequests.delete(requestId);
          resolve(forceRefresh ? undefined : fallbackToken);
        }, timeoutMs);

        pendingRequests.set(requestId, {
          fallbackToken,
          forceRefresh,
          resolve,
          timeout,
        });

        postMessage(JSON.stringify({
          type: SURVEY_WEBVIEW_AUTH_TOKEN_REQUEST,
          requestId,
          forceRefresh,
        } satisfies SurveyWebViewAuthTokenRequest));
      });
    },

    dispose() {
      unsubscribe();

      for (const pending of pendingRequests.values()) {
        clearTimeout(pending.timeout);
        pending.resolve(pending.forceRefresh ? undefined : pending.fallbackToken);
      }

      pendingRequests.clear();
    },
  };
};

let browserTokenClient: ReturnType<typeof createSurveyWebViewAuthTokenClient> | undefined;

export const supportsSurveyWebViewAuthTokenBridge = (search: string): boolean => new URLSearchParams(search)
  .get('authTokenBridge') === 'true';

export const getInjectedSurveyWebViewToken = (search: string): string | undefined => {
  const params = new URLSearchParams(search);
  return params.get('webview') === 'true' ? params.get('token') ?? undefined : undefined;
};

export const shouldPreserveInjectedSurveyToken = (firebaseUser: unknown, injectedToken?: string | null): boolean =>
  firebaseUser == null && !!injectedToken;

export const shouldHydrateInjectedSurveyToken = (
  currentToken?: string,
  injectedToken?: string | null,
): injectedToken is string => !!injectedToken && currentToken !== injectedToken;

export const subscribeToSurveyWebViewAuthTokenResponses = (
  listener: (data: unknown) => void,
  windowTarget: SurveyWebViewMessageEventTarget,
  documentTarget: SurveyWebViewMessageEventTarget,
) => {
  const handleMessage = (event: Event) => listener((event as MessageEvent).data);

  windowTarget.addEventListener('message', handleMessage);
  documentTarget.addEventListener('message', handleMessage);

  return () => {
    windowTarget.removeEventListener('message', handleMessage);
    documentTarget.removeEventListener('message', handleMessage);
  };
};

export const createSurveyWebViewAuthBridgeClient = ({
  search,
  nativeBridge,
  windowTarget,
  documentTarget,
}: SurveyWebViewAuthBridgeOptions) => {
  if (!supportsSurveyWebViewAuthTokenBridge(search) || !nativeBridge?.postMessage) {
    return undefined;
  }

  return createSurveyWebViewAuthTokenClient({
    postMessage: (message) => nativeBridge.postMessage!(message),
    subscribe: (listener) => subscribeToSurveyWebViewAuthTokenResponses(listener, windowTarget, documentTarget),
  });
};

export const requestSurveyWebViewAuthTokenWithClient = (
  client: ReturnType<typeof createSurveyWebViewAuthTokenClient> | undefined,
  fallbackToken?: string,
  forceRefresh = false,
): Promise<string | undefined> => client?.requestToken(fallbackToken, forceRefresh)
  ?? Promise.resolve(forceRefresh ? undefined : fallbackToken);

const getBrowserTokenClient = () => {
  if (browserTokenClient || typeof window === 'undefined') {
    return browserTokenClient;
  }

  const nativeBridge = (window as any).ReactNativeWebView as { postMessage?(message: string): void } | undefined;
  browserTokenClient = createSurveyWebViewAuthBridgeClient({
    search: window.location.search,
    nativeBridge,
    windowTarget: window,
    documentTarget: document,
  });

  return browserTokenClient;
};

export const requestSurveyWebViewAuthToken = (
  fallbackToken?: string,
  forceRefresh = false,
): Promise<string | undefined> => requestSurveyWebViewAuthTokenWithClient(
  getBrowserTokenClient(),
  fallbackToken,
  forceRefresh,
);
